import {
  ChangeEventHandler,
  FocusEventHandler,
  KeyboardEventHandler,
  ReactNode,
  Ref,
} from 'react';

import { useUniqueId } from 'utils/hooks';

import {
  InputAffixLabel,
  InputContainer,
  InputElement,
  InputPrefixVariant,
  InputStatus,
  InputStyleVariant,
} from './Input.styled';

type InputProps = {
  autoComplete?: string;
  id?: string;
  name?: string;
  ref?: Ref<HTMLInputElement>;
  title?: string;
  placeholder?: string;
  value?: string;
  disabled?: boolean;
  readOnly?: boolean;
  autoFocus?: boolean;
  qaId?: string;
  ariaLabel?: string;
  ariaAutocomplete?: 'none' | 'inline' | 'list' | 'both' | undefined;
  ariaControls?: string;
  ariaActiveDescendant?: string;
  type?: string;
  onChange?: ChangeEventHandler<HTMLInputElement>;
  onBlur?: FocusEventHandler<HTMLInputElement>;
  onKeyDown?: KeyboardEventHandler<HTMLInputElement>;
  onKeyPress?: KeyboardEventHandler<HTMLInputElement>;
  onKeyUp?: KeyboardEventHandler<HTMLInputElement>;
  onFocus?: FocusEventHandler<HTMLInputElement>;
};

type Props = {
  inputProps: InputProps;
  prefix?: ReactNode;
  suffix?: ReactNode;
  status?: InputStatus;
  prefixVariant?: InputPrefixVariant;
  styleVariant?: InputStyleVariant;
  fullHeight?: boolean;
  fullWidth?: boolean;
};

export function Input({
  inputProps,
  prefix,
  suffix,
  status = 'neutral',
  prefixVariant = 'default',
  styleVariant = 'default',
  fullHeight,
  fullWidth,
}: Props) {
  const inputId = useUniqueId({
    prefix: 'input-group-',
    override: inputProps.id,
  });

  return (
    <InputContainer
      $status={status}
      $prefixVariant={prefixVariant}
      $styleVariant={styleVariant}
      $fullHeight={Boolean(fullHeight)}
      $fullWidth={Boolean(fullWidth)}
    >
      {prefix && <InputAffixLabel htmlFor={inputId}>{prefix}</InputAffixLabel>}

      <InputElement
        id={inputId}
        name={inputProps.name}
        ref={inputProps.ref}
        title={inputProps.title}
        placeholder={inputProps.placeholder}
        value={inputProps.value}
        onChange={inputProps.onChange}
        onBlur={inputProps.onBlur}
        onKeyPress={inputProps.onKeyPress}
        onKeyUp={inputProps.onKeyUp}
        onKeyDown={inputProps.onKeyDown}
        onFocus={inputProps.onFocus}
        disabled={inputProps.disabled}
        readOnly={inputProps.readOnly}
        autoFocus={inputProps.autoFocus}
        data-qa-id={inputProps.qaId}
        aria-label={inputProps.ariaLabel}
        aria-autocomplete={inputProps.ariaAutocomplete}
        aria-controls={inputProps.ariaControls}
        aria-activedescendant={inputProps.ariaActiveDescendant}
      />

      {suffix && <InputAffixLabel htmlFor={inputId}>{suffix}</InputAffixLabel>}
    </InputContainer>
  );
}
